import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Header, API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Settings, Database, FileJson, List, RefreshCw, Upload, 
  ChevronRight, Eye, Sliders, TestTube, Download, Calendar, MapPin, Clock,
  BarChart3, TrendingUp, Users, LogOut, MessageSquare, ArrowUpRight, ArrowDownRight,
  Mail, Phone, User, Image, Camera
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Auth helper — uses token from sessionStorage + Authorization header
const getAuthConfig = () => {
  const token = sessionStorage.getItem("admin_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : { withCredentials: true };
};

// Offers List Component
const OffersList = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offerDetail, setOfferDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/offers`, getAuthConfig());
      setOffers(response.data.offers);
    } catch (err) {
      toast.error('Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfferDetail = async (offerId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/offers/${offerId}`, getAuthConfig());
      setOfferDetail(res.data);
    } catch {
      setOfferDetail(null);
      toast.error("Failed to load offer details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectOffer = (offer) => {
    if (selectedOffer?.offer_id === offer.offer_id) {
      setSelectedOffer(null);
      setOfferDetail(null);
    } else {
      setSelectedOffer(offer);
      fetchOfferDetail(offer.offer_id);
    }
  };

  useEffect(() => {
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0 
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-heading-3">Offers</h2>
        <Button onClick={fetchOffers} variant="outline" size="sm" data-testid="refresh-offers-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="skeleton h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : offers.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          No offers yet. Generate one from the main app.
        </div>
      ) : (
        <div className="space-y-3">
          {offers.map((offer) => (
            <div 
              key={offer.offer_id}
              className="surface-card p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleSelectOffer(offer)}
              data-testid={`offer-item-${offer.offer_id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">
                    {offer.vehicle_info?.year} {offer.vehicle_info?.make} {offer.vehicle_info?.model}
                  </p>
                  <p className="text-sm text-slate-500 font-mono">{offer.vin}</p>
                  {offer.contact?.name && (
                    <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {offer.contact.name}
                      {offer.contact.email && <span className="ml-1">({offer.contact.email})</span>}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-semibold text-lg text-blue-600">
                    {formatCurrency(offer.instant_offer)}
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    offer.status === 'locked' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {offer.status || 'pending'}
                  </span>
                </div>
              </div>

              {selectedOffer?.offer_id === offer.offer_id && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                  {/* Synthetic Corridor Breakdown */}
                  {offer.breakdown?.synthetic_corridor && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-slate-900 mb-3">Synthetic Corridor</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Shadow Floor (30th)</p>
                          <p className="font-semibold text-slate-900">{formatCurrency(offer.breakdown.synthetic_corridor.shadow_floor)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Shadow Mid (50th)</p>
                          <p className="font-semibold text-blue-600">{formatCurrency(offer.breakdown.synthetic_corridor.shadow_mid)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Shadow Stretch (70th)</p>
                          <p className="font-semibold text-slate-900">{formatCurrency(offer.breakdown.synthetic_corridor.shadow_stretch)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Offer Calculation */}
                  {offer.breakdown?.offer_calculation && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-slate-900 mb-3">Offer Calculation</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">ICO Raw</span>
                          <span>{formatCurrency(offer.breakdown.offer_calculation.ico_raw)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Market Boost</span>
                          <span>{((offer.breakdown.market_boost?.market_boost_pct || 0) * 100).toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">ICO Boosted</span>
                          <span>{formatCurrency(offer.breakdown.offer_calculation.ico_boosted)}</span>
                        </div>
                        <div className="flex justify-between text-red-600">
                          <span>Initial Holdback (25%)</span>
                          <span>-{formatCurrency(offer.breakdown.offer_calculation.initial_unknown_holdback)}</span>
                        </div>
                        <div className="flex justify-between text-emerald-600">
                          <span>Rooftop Uplift</span>
                          <span>+{formatCurrency(offer.breakdown.offer_calculation.rooftop_uplift)}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-2">
                          <span>ICO Final</span>
                          <span className="text-blue-600">{formatCurrency(offer.breakdown.offer_calculation.ico_final)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recon Details */}
                  {offer.breakdown?.recon && (
                    <div className="bg-amber-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-slate-900 mb-3">Reconditioning Holdback</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Estimated Total Recon</span>
                          <span>{formatCurrency(offer.breakdown.recon.estimated_total)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Initial Holdback</span>
                          <span className="text-red-600">-{formatCurrency(offer.breakdown.recon.initial_holdback)}</span>
                        </div>
                        {offer.breakdown.recon.lock_recon_included != null ? (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Lock Assessment (in offer, adjusted at lock)</span>
                            <span className="text-red-600">-{formatCurrency(offer.breakdown.recon.lock_recon_included)}</span>
                          </div>
                        ) : (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Remaining for Lock</span>
                            <span className="text-amber-600">{formatCurrency(offer.breakdown.recon.remaining_for_lock)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Market & Option Boost */}
                  <div className="grid grid-cols-2 gap-4">
                    {offer.breakdown?.market_boost && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-semibold text-sm text-slate-900 mb-2">Market Boost</h4>
                        <p className="text-2xl font-bold text-blue-600">{((offer.breakdown.market_boost.market_boost_pct || 0) * 100).toFixed(1)}%</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Year Demand: {offer.breakdown.market_boost.year_demand?.toFixed(0) || 'N/A'}
                        </p>
                      </div>
                    )}
                    {offer.breakdown?.option_boost && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-semibold text-sm text-slate-900 mb-2">Option Boost</h4>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(offer.breakdown.option_boost.option_boost || 0)}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {(offer.breakdown.option_boost.components || []).length} premium features
                        </p>
                      </div>
                    )}
                  </div>

                  {/* TradePending Reference */}
                  {offer.breakdown?.tradepending_reference && (
                    <div className="bg-slate-100 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-slate-900 mb-2">TradePending Reference <span className="text-xs font-normal text-slate-500">(not primary anchor)</span></h4>
                      <div className="flex gap-6 text-sm">
                        <div>
                          <span className="text-slate-500">Stats Low:</span> {formatCurrency(offer.breakdown.tradepending_reference.stats_low || 0)}
                        </div>
                        <div>
                          <span className="text-slate-500">Stats Value:</span> <span className="font-medium">{formatCurrency(offer.breakdown.tradepending_reference.stats_value || 0)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Stats High:</span> {formatCurrency(offer.breakdown.tradepending_reference.stats_high || 0)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Customer Contact Info */}
                  {(offer.contact || offerDetail?.contact) && (
                    <div className="bg-violet-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4 text-violet-600" />
                        Customer Contact
                      </h4>
                      <div className="space-y-2 text-sm">
                        {(offer.contact?.name || offerDetail?.contact?.name) && (
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-slate-900 font-medium">{offer.contact?.name || offerDetail?.contact?.name}</span>
                          </div>
                        )}
                        {(offer.contact?.email || offerDetail?.contact?.email) && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            <a href={`mailto:${offer.contact?.email || offerDetail?.contact?.email}`} className="text-blue-600 hover:underline">
                              {offer.contact?.email || offerDetail?.contact?.email}
                            </a>
                          </div>
                        )}
                        {(offer.contact?.phone || offerDetail?.contact?.phone) && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                            <a href={`tel:${offer.contact?.phone || offerDetail?.contact?.phone}`} className="text-blue-600 hover:underline">
                              {offer.contact?.phone || offerDetail?.contact?.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Appointment Info */}
                  {offerDetail?.appointment && (
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-emerald-600" />
                        Scheduled Appointment
                      </h4>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-slate-900">{offerDetail.appointment.location?.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-slate-600">
                          <span>{offerDetail.appointment.date}</span>
                          <span>{offerDetail.appointment.time_slot}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Photos */}
                  {offerDetail?.photos?.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
                        <Camera className="w-4 h-4 text-slate-600" />
                        Condition Photos ({offerDetail.photos.length})
                      </h4>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {offerDetail.photos.map((photo) => (
                          <a
                            key={photo.photo_id}
                            href={`${API}/offers/photos/${photo.photo_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="aspect-square rounded-lg overflow-hidden bg-slate-200 hover:ring-2 hover:ring-blue-400 transition-all"
                            data-testid={`admin-photo-${photo.photo_id}`}
                          >
                            <img
                              src={`${API}/offers/photos/${photo.photo_id}`}
                              alt={photo.photo_type || "Vehicle photo"}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </a>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {offerDetail.photos.map((photo) => (
                          <span key={`label-${photo.photo_id}`} className="text-xs px-2 py-0.5 bg-white rounded text-slate-500">
                            {photo.photo_type || "photo"}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {detailLoading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
                      <span className="ml-2 text-sm text-slate-500">Loading details...</span>
                    </div>
                  )}

                  {/* Full JSON */}
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700">View Full JSON</summary>
                    <pre className="json-viewer text-xs overflow-auto max-h-64 mt-2">
                      {JSON.stringify(offer, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Pricing Config Component
const PricingConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/config`, getAuthConfig());
      setConfig(response.data);
    } catch (err) {
      toast.error('Failed to fetch config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, config, getAuthConfig());
      toast.success('Configuration saved');
    } catch (err) {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  if (loading) {
    return <div className="skeleton h-64 w-full rounded-xl" />;
  }

  const configFields = [
    // Synthetic Corridor Weights
    { key: 'shadow_mid_weight', label: 'Shadow Mid Weight', description: 'Weight for 50th percentile (primary anchor)', section: 'corridor' },
    { key: 'shadow_floor_weight', label: 'Shadow Floor Weight', description: 'Weight for 30th percentile (conservative)', section: 'corridor' },
    { key: 'shadow_stretch_weight', label: 'Shadow Stretch Weight', description: 'Weight for 70th percentile (optimistic)', section: 'corridor' },
    { key: 'option_boost_weight', label: 'Option Boost Weight', description: 'Weight for premium options', section: 'corridor' },
    
    // Adjustment Parameters
    { key: 'mileage_adjustment_per_mile', label: 'Mileage Adj $/mile', description: 'Dollar adjustment per mile difference', section: 'adjustments' },
    { key: 'distance_adjustment_base', label: 'Distance Adj Base ($)', description: 'Base deduction for distant comps', section: 'adjustments' },
    { key: 'trim_match_bonus', label: 'Trim Match Bonus ($)', description: 'Bonus for exact trim match', section: 'adjustments' },
    
    // Recon Holdback
    { key: 'initial_recon_holdback_pct', label: 'Initial Recon Holdback %', description: 'Percent of recon applied upfront (default 25%)', section: 'recon' },
    { key: 'max_initial_holdback', label: 'Max Initial Holdback ($)', description: 'Cap on initial holdback', section: 'recon' },
    { key: 'estimated_recon_total', label: 'Default Est. Recon ($)', description: 'Default estimated reconditioning', section: 'recon' },
    
    // Market Boost Thresholds
    { key: 'year_demand_high_threshold', label: 'Year Demand High Threshold', description: 'Threshold for year demand boost (default 90)', section: 'market' },
    { key: 'demand_high_threshold', label: 'Demand High Threshold', description: 'Threshold for vehicle demand boost (default 70)', section: 'market' },
    { key: 'supply_high_threshold', label: 'Supply High Threshold', description: 'Threshold for supply penalty (default 70)', section: 'market' },
    
    // Rooftop
    { key: 'rooftop_appetite_uplift', label: 'Rooftop Appetite Uplift ($)', description: 'Uplift based on inventory needs', section: 'rooftop' },
  ];

  const sections = {
    corridor: 'Synthetic Corridor Weights',
    adjustments: 'Comp Adjustment Parameters',
    recon: 'Reconditioning Holdback',
    market: 'Market Boost Thresholds',
    rooftop: 'Rooftop Settings'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-heading-3">Pricing Configuration</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {Object.entries(sections).map(([sectionKey, sectionLabel]) => (
        <div key={sectionKey} className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{sectionLabel}</h3>
          <div className="grid gap-3">
            {configFields.filter(f => f.section === sectionKey).map((field) => (
              <div key={field.key} className="surface-card p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <label className="font-medium text-slate-900">{field.label}</label>
                    <p className="text-sm text-slate-500">{field.description}</p>
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={config?.[field.key] ?? ''}
                    onChange={(e) => updateConfig(field.key, e.target.value)}
                    className="w-32 text-right"
                    data-testid={`config-${field.key}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="surface-card p-4">
        <h3 className="font-medium text-slate-900 mb-2">Synthetic Corridor ICO Formula</h3>
        <pre className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg overflow-auto">
{`1. Build Synthetic Corridor from Similar Vehicles:
   adjusted_trade_i = trade_value + mileage_adj + distance_adj + trim_bonus
   shadow_floor   = weighted_percentile(adjusted_trades, 0.30)
   shadow_mid     = weighted_percentile(adjusted_trades, 0.50)
   shadow_stretch = weighted_percentile(adjusted_trades, 0.70)

2. ICO Raw Calculation:
   ICO_raw = ${config?.shadow_mid_weight || 0.55} * shadow_mid
           + ${config?.shadow_floor_weight || 0.20} * shadow_floor
           + ${config?.shadow_stretch_weight || 0.15} * shadow_stretch
           + option_boost

3. Market Boost (if demand >= ${config?.demand_high_threshold || 70}):
   market_boost_pct = year_demand_boost + vehicle_demand_boost - supply_penalty

4. Initial Holdback (${((config?.initial_recon_holdback_pct || 0.25) * 100).toFixed(0)}% of est. recon, max $${config?.max_initial_holdback || 450}):
   initial_holdback = min($${config?.max_initial_holdback || 450}, est_recon * ${config?.initial_recon_holdback_pct || 0.25})

5. Lock Assessment (50% of est. recon, deducted up front):
   lock_recon = est_recon * 0.50

6. Final ICO:
   ICO_final = max(shadow_floor, (ICO_raw * (1 + market_boost_pct)) - holdback - lock_recon + $${config?.rooftop_appetite_uplift || 300})

7. Lock Stage: lock_recon re-scaled by condition (0.5x / 0.75x / 1.0x / 1.25x); the difference is credited or charged`}
        </pre>
      </div>
    </div>
  );
};

// Seed Data Component
const SeedData = () => {
  const [seeding, setSeeding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payloads, setPayloads] = useState([]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/seed`, null, getAuthConfig());
      toast.success(response.data.message);
    } catch (err) {
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const authConfig = getAuthConfig();
      const response = await axios.post(`${API}/admin/upload-payload`, formData, {
        ...authConfig,
        headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Payload uploaded: ${response.data.filename}`);
      fetchPayloads();
    } catch (err) {
      toast.error('Failed to upload payload');
    } finally {
      setUploading(false);
    }
  };

  const fetchPayloads = async () => {
    try {
      const response = await axios.get(`${API}/admin/payloads`, getAuthConfig());
      setPayloads(response.data.payloads);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch payloads:", err);
    }
  };

  useEffect(() => {
    fetchPayloads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <h2 className="text-heading-3">Test Data</h2>

      {/* Seed Default Data */}
      <div className="surface-card p-6">
        <h3 className="font-medium text-slate-900 mb-2">Seed Default Data</h3>
        <p className="text-sm text-slate-500 mb-4">
          Creates sample vehicles and default pricing configuration for testing.
        </p>
        <Button onClick={handleSeed} disabled={seeding} data-testid="seed-data-btn">
          <Database className="w-4 h-4 mr-2" />
          {seeding ? 'Seeding...' : 'Seed Data'}
        </Button>
      </div>

      {/* Upload Payload */}
      <div className="surface-card p-6">
        <h3 className="font-medium text-slate-900 mb-2">Upload Test Payload</h3>
        <p className="text-sm text-slate-500 mb-4">
          Upload a JSON payload file for testing custom scenarios.
        </p>
        <label className="inline-flex">
          <input
            type="file"
            accept=".json"
            onChange={handleUpload}
            className="hidden"
            data-testid="upload-payload-input"
          />
          <Button as="span" variant="outline" disabled={uploading}>
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload JSON'}
          </Button>
        </label>
      </div>

      {/* Uploaded Payloads */}
      {payloads.length > 0 && (
        <div className="surface-card p-6">
          <h3 className="font-medium text-slate-900 mb-4">Uploaded Payloads</h3>
          <div className="space-y-2">
            {payloads.map((p) => (
              <div key={p.filename} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileJson className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium">{p.filename}</span>
                </div>
                <span className="text-xs text-slate-500">
                  {new Date(p.uploaded_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sample Payload Format */}
      <div className="surface-card p-6">
        <h3 className="font-medium text-slate-900 mb-2">Sample Payload Format</h3>
        <pre className="json-viewer text-xs">
{`{
  "vehicle": {
    "vin": "1HGBH41JXMN109186",
    "year": 2021,
    "make": "Honda",
    "model": "Accord",
    "trim": "Sport",
    "msrp": 28500
  },
  "comps": [
    {
      "trade_value": 22000,
      "retail_price": 26000,
      "mileage": 45000,
      "distance_miles": 25,
      "trim_match": true,
      "powertrain_match": true
    }
  ],
  "market_data": {
    "demand_index": 1.05,
    "supply_days": 45
  }
}`}
        </pre>
      </div>
    </div>
  );
};

// Appointments List Component
const AppointmentsList = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/appointments`, getAuthConfig());
      setAppointments(response.data.appointments);
    } catch (err) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-heading-3">Appointments</h2>
        <Button onClick={fetchAppointments} variant="outline" size="sm" data-testid="refresh-appointments-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="skeleton h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          No appointments scheduled yet.
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map((appt) => (
            <div key={appt.appointment_id} className="surface-card p-5" data-testid={`appointment-item-${appt.appointment_id}`}>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="font-medium text-slate-900">
                    {appt.vehicle_info?.year} {appt.vehicle_info?.make} {appt.vehicle_info?.model}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{appt.location?.name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {appt.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-slate-400" />
                      {appt.time_slot}
                    </span>
                  </div>
                  {appt.contact && (
                    <div className="text-sm text-slate-500">
                      {appt.contact.name && <span className="mr-3">{appt.contact.name}</span>}
                      {appt.contact.email && <span className="mr-3">{appt.contact.email}</span>}
                      {appt.contact.phone && <span>{appt.contact.phone}</span>}
                    </div>
                  )}
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                  {appt.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Analytics Dashboard
const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`, getAuthConfig());
      setData(res.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-xl" />)}
        </div>
        <div className="skeleton h-64 rounded-xl" />
      </div>
    );
  }

  const { funnel, conversion, period, daily } = data;
  const maxOffers = Math.max(...daily.map(d => d.offers), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-heading-3">Dashboard</h2>
        <Button onClick={fetchAnalytics} variant="outline" size="sm" data-testid="refresh-analytics-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Funnel KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "VINs Decoded", value: funnel.decodes, sub: `${period.offers_week} this week`, color: "bg-blue-50 text-blue-700" },
          { label: "Offers Generated", value: funnel.offers, sub: `${conversion.decode_to_offer}% conversion`, color: "bg-emerald-50 text-emerald-700" },
          { label: "Offers Locked", value: funnel.locked, sub: `${conversion.offer_to_lock}% lock rate`, color: "bg-amber-50 text-amber-700" },
          { label: "Appointments", value: funnel.appointments, sub: `${period.appointments_week} this week`, color: "bg-violet-50 text-violet-700" },
        ].map((kpi) => (
          <div key={kpi.label} className="surface-card p-5" data-testid={`kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}>
            <p className="text-sm text-slate-500 font-medium">{kpi.label}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{kpi.value.toLocaleString()}</p>
            <p className={`text-xs mt-1.5 px-2 py-0.5 rounded-full inline-block ${kpi.color}`}>{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Conversion Funnel */}
      <div className="surface-card p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Conversion Funnel</h3>
        <div className="flex items-center gap-2">
          {[
            { label: "Decode", val: funnel.decodes, rate: null },
            { label: "Offer", val: funnel.offers, rate: conversion.decode_to_offer },
            { label: "Lock", val: funnel.locked, rate: conversion.offer_to_lock },
            { label: "Appt", val: funnel.appointments, rate: conversion.lock_to_appointment },
          ].map((step, i) => (
            <div key={step.label} className="flex items-center gap-2 flex-1">
              <div className="flex-1 text-center">
                <p className="text-xs text-slate-500 mb-1">{step.label}</p>
                <div className="h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white" style={{
                  backgroundColor: `hsl(220, 70%, ${15 + i * 12}%)`,
                  width: `${Math.max(20, (step.val / Math.max(funnel.decodes, 1)) * 100)}%`,
                  minWidth: "40px",
                  margin: "0 auto",
                }}>
                  {step.val}
                </div>
                {step.rate !== null && (
                  <p className="text-xs text-slate-400 mt-1">{step.rate}%</p>
                )}
              </div>
              {i < 3 && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* Daily Chart */}
      <div className="surface-card p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Last 14 Days</h3>
        <div className="flex items-end gap-1.5" style={{ height: "140px" }}>
          {daily.map((day) => (
            <div key={day.date} className="flex-1 flex flex-col items-center gap-1 justify-end h-full">
              <div className="w-full flex flex-col items-center justify-end flex-1">
                {day.locked > 0 && (
                  <div className="w-full rounded-t" style={{ height: `${Math.max(4, (day.locked / maxOffers) * 100)}%`, backgroundColor: "hsl(48, 96%, 53%)" }} />
                )}
                <div className="w-full rounded-t" style={{ height: `${Math.max(4, (day.offers / maxOffers) * 100)}%`, backgroundColor: "hsl(220, 70%, 15%)" }} />
              </div>
              <span className="text-[10px] text-slate-400 whitespace-nowrap">{day.date.replace(/\w+ /, "")}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: "hsl(220, 70%, 15%)" }} /> Offers</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: "hsl(48, 96%, 53%)" }} /> Locked</span>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="surface-card p-5">
          <p className="text-sm text-slate-500">Offers Today</p>
          <p className="text-2xl font-bold text-slate-900">{period.offers_today}</p>
        </div>
        <div className="surface-card p-5">
          <p className="text-sm text-slate-500">Contact Submissions</p>
          <p className="text-2xl font-bold text-slate-900">{data.totals.contacts}</p>
        </div>
        <div className="surface-card p-5">
          <p className="text-sm text-slate-500">Recall Checks</p>
          <p className="text-2xl font-bold text-slate-900">{data.totals.recall_checks}</p>
        </div>
      </div>
    </div>
  );
};

// Contacts List
const ContactsList = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, getAuthConfig()).then(res => {
      setContacts(res.data.contacts);
    }).catch(() => { toast.error("Failed to load contacts"); }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <h2 className="text-heading-3">Contact Submissions</h2>
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 text-slate-500">No contact submissions yet.</div>
      ) : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div key={c.submission_id} className="surface-card p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-slate-900">{c.name}</p>
                  <p className="text-sm text-slate-500">{c.email} {c.phone && `| ${c.phone}`}</p>
                  <p className="text-sm text-slate-600 mt-1">{c.message}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">{c.subject}</span>
                  <p className="text-xs text-slate-400 mt-1">{new Date(c.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Panel
const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Verify auth via token
    const token = sessionStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        sessionStorage.setItem("admin_user", JSON.stringify({ email: res.data.email, name: res.data.name }));
        setAuthed(true);
        setChecking(false);
      })
      .catch(() => {
        sessionStorage.removeItem("admin_token");
        sessionStorage.removeItem("admin_user");
        navigate("/admin/login");
      });
  }, [navigate]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* logout cleanup continues regardless */ }
    sessionStorage.removeItem("admin_token");
    sessionStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  if (checking || !authed) return null;

  const adminUser = JSON.parse(sessionStorage.getItem("admin_user") || "{}");

  return (
    <>
      <Header />
      
      <main className="min-h-[calc(100vh-80px)]">
        <div className="container-wide py-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6 text-slate-400" />
              <h1 className="text-heading-2">Admin Panel</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">{adminUser.email}</span>
              <Button onClick={handleLogout} variant="outline" size="sm" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-slate-100 p-1 rounded-xl flex-wrap">
              <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-dashboard">
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="offers" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-offers">
                <List className="w-4 h-4 mr-2" />
                Offers
              </TabsTrigger>
              <TabsTrigger value="config" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-config">
                <Sliders className="w-4 h-4 mr-2" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="appointments" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-appointments">
                <Calendar className="w-4 h-4 mr-2" />
                Appointments
              </TabsTrigger>
              <TabsTrigger value="contacts" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-contacts">
                <MessageSquare className="w-4 h-4 mr-2" />
                Contacts
              </TabsTrigger>
              <TabsTrigger value="seed" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-seed">
                <TestTube className="w-4 h-4 mr-2" />
                Test Data
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard"><AnalyticsDashboard /></TabsContent>
            <TabsContent value="offers"><OffersList /></TabsContent>
            <TabsContent value="config"><PricingConfig /></TabsContent>
            <TabsContent value="appointments"><AppointmentsList /></TabsContent>
            <TabsContent value="contacts"><ContactsList /></TabsContent>
            <TabsContent value="seed"><SeedData /></TabsContent>
          </Tabs>
        </div>
      </main>
    </>
  );
};

export default AdminPanel;
